import { createSlice } from "@reduxjs/toolkit";
import {
  fetchCommentsByPost,
  createComment,
  updateComment,
  deleteComment,
} from "../thunks/commentsThunks";

/* ✅ Helper: normalize comment id (Mongo uses _id) */
const getId = (c) => String(c?._id ?? c?.id ?? "");

/* ✅ Helper: normalize errors */
const getErrMsg = (action, fallback) =>
  action?.payload?.message ||
  action?.payload?.details?.message ||
  action?.error?.message ||
  fallback;

const initialState = {
  byPostId: {},
  loading: false,
  creating: false,
  updating: false,
  deleting: false,
  error: null,
  createError: null,
  updateError: null,
  deleteError: null,
};

const commentsSlice = createSlice({
  name: "comments",
  initialState,
  reducers: {
    clearCommentsState: (state) => {
      state.byPostId = {};
      state.loading = false;
      state.creating = false;
      state.updating = false;
      state.deleting = false;
      state.error = null;
      state.createError = null;
      state.updateError = null;
      state.deleteError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      /* =====================
         Fetch comments for post
         ===================== */
      .addCase(fetchCommentsByPost.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCommentsByPost.fulfilled, (state, action) => {
        state.loading = false;
        const { postId, comments } = action.payload || {};
        if (!postId) return;

        state.byPostId[postId] = Array.isArray(comments) ? comments : [];
      })
      .addCase(fetchCommentsByPost.rejected, (state, action) => {
        state.loading = false;
        state.error = getErrMsg(action, "Failed to load comments");
      })

      /* =====================
         Create comment
         ===================== */
      .addCase(createComment.pending, (state) => {
        state.creating = true;
        state.createError = null;
      })
      .addCase(createComment.fulfilled, (state, action) => {
        state.creating = false;
        const { postId, comment } = action.payload || {};
        if (!postId || !comment) return;

        if (!state.byPostId[postId]) {
          state.byPostId[postId] = [];
        }

        const newId = getId(comment);

        if (newId) {
          const exists = state.byPostId[postId].some(
            (c) => getId(c) === newId
          );
          if (!exists) {
            state.byPostId[postId].unshift(comment);
          }
        } else {
          state.byPostId[postId].unshift(comment);
        }
      })
      .addCase(createComment.rejected, (state, action) => {
        state.creating = false;
        state.createError = getErrMsg(action, "Failed to create comment");
      })

      /* =====================
         Update comment
         ===================== */
      .addCase(updateComment.pending, (state) => {
        state.updating = true;
        state.updateError = null;
      })
      .addCase(updateComment.fulfilled, (state, action) => {
        state.updating = false;
        const { postId, commentId, content } = action.payload || {};
        if (!postId || !commentId) return;

        const comments = state.byPostId[postId];
        if (!Array.isArray(comments)) return;

        const targetId = String(commentId);
        const c = comments.find((x) => getId(x) === targetId);

        if (c) {
          c.content = content;
          c.updatedAt = new Date().toISOString();
        }
      })
      .addCase(updateComment.rejected, (state, action) => {
        state.updating = false;
        state.updateError = getErrMsg(action, "Failed to update comment");
      })

      /* =====================
         Delete comment
         ===================== */
      .addCase(deleteComment.pending, (state) => {
        state.deleting = true;
        state.deleteError = null;
      })
      .addCase(deleteComment.fulfilled, (state, action) => {
        state.deleting = false;
        const { postId, commentId } = action.payload || {};
        if (!postId || !commentId) return;

        const comments = state.byPostId[postId];
        if (!Array.isArray(comments)) return;

        const targetId = String(commentId);
        state.byPostId[postId] = comments.filter(
          (c) => getId(c) !== targetId
        );
      })
      .addCase(deleteComment.rejected, (state, action) => {
        state.deleting = false;
        state.deleteError = getErrMsg(action, "Failed to delete comment");
      });
  },
});

export const { clearCommentsState } = commentsSlice.actions;
export default commentsSlice.reducer;